import { MediaUploadUsage, type MediaUploadUsageValue } from '@/lib/enums/MediaUploadUsage'
import type { PhotoFolder } from '@/lib/services/photo-manager/photo-manager.types'

export const PhotoManagerMode = {
  UploadOnly: 'upload_only',
  UploadOnlyMultiple: 'upload_only_multiple',
  UploadOnlySaved: 'upload_only_saved',
  Full: 'full',
  StockOnly: 'stock_only',
  UploadAndStock: 'upload_and_stock',
} as const

export type PhotoManagerModeValue = (typeof PhotoManagerMode)[keyof typeof PhotoManagerMode]

export const PhotoManagerSelectionMode = {
  Single: 'single',
  Multiple: 'multiple',
} as const

export type PhotoManagerSelectionModeValue =
  (typeof PhotoManagerSelectionMode)[keyof typeof PhotoManagerSelectionMode]

export const PhotoManagerDefaultUploadDestination = {
  ProductPhotos: 'product_photos',
  BannerImages: 'banner_images',
} as const

export type PhotoManagerDefaultUploadDestinationValue =
  (typeof PhotoManagerDefaultUploadDestination)[keyof typeof PhotoManagerDefaultUploadDestination]

export const PhotoManagerUploadMode = {
  UseHere: 'use_here',
  SaveOnly: 'save_only',
} as const

/** The photo manager folder an upload destination saves into. */
export interface PhotoManagerDefaultUploadDestinationConfig {
  name: string
}

/** Which tabs a photo manager shows and how its uploads behave. */
export interface PhotoManagerConfig {
  mode: PhotoManagerModeValue
  tabs: {
    upload: boolean
    uploads: boolean
    stock: boolean
  }
  allowMultipleUploads: boolean
  attachUploadsToPhotoManager: boolean
  showUploadDestination: boolean
}

const PHOTO_MANAGER_DEFAULT_UPLOAD_DESTINATIONS: Record<
  PhotoManagerDefaultUploadDestinationValue,
  PhotoManagerDefaultUploadDestinationConfig
> = {
  [PhotoManagerDefaultUploadDestination.ProductPhotos]: {
    name: 'Product Photos',
  },
  [PhotoManagerDefaultUploadDestination.BannerImages]: {
    name: 'Banner Images',
  },
}

const PHOTO_MANAGER_CONFIGS: Record<PhotoManagerModeValue, PhotoManagerConfig> = {
  [PhotoManagerMode.UploadOnly]: {
    mode: PhotoManagerMode.UploadOnly,
    tabs: {
      upload: true,
      uploads: false,
      stock: false,
    },
    allowMultipleUploads: false,
    attachUploadsToPhotoManager: false,
    showUploadDestination: false,
  },
  [PhotoManagerMode.UploadOnlyMultiple]: {
    mode: PhotoManagerMode.UploadOnlyMultiple,
    tabs: {
      upload: true,
      uploads: false,
      stock: false,
    },
    allowMultipleUploads: true,
    attachUploadsToPhotoManager: false,
    showUploadDestination: false,
  },
  [PhotoManagerMode.UploadOnlySaved]: {
    mode: PhotoManagerMode.UploadOnlySaved,
    tabs: {
      upload: true,
      uploads: false,
      stock: false,
    },
    allowMultipleUploads: false,
    attachUploadsToPhotoManager: true,
    showUploadDestination: false,
  },
  [PhotoManagerMode.Full]: {
    mode: PhotoManagerMode.Full,
    tabs: {
      upload: true,
      uploads: true,
      stock: true,
    },
    allowMultipleUploads: true,
    attachUploadsToPhotoManager: true,
    showUploadDestination: true,
  },
  [PhotoManagerMode.StockOnly]: {
    mode: PhotoManagerMode.StockOnly,
    tabs: {
      upload: false,
      uploads: false,
      stock: true,
    },
    allowMultipleUploads: false,
    attachUploadsToPhotoManager: false,
    showUploadDestination: false,
  },
  [PhotoManagerMode.UploadAndStock]: {
    mode: PhotoManagerMode.UploadAndStock,
    tabs: {
      upload: true,
      uploads: false,
      stock: true,
    },
    allowMultipleUploads: false,
    attachUploadsToPhotoManager: false,
    showUploadDestination: false,
  },
}

export function getPhotoManagerConfig({
  mode,
  uploadUsage,
}: {
  mode?: PhotoManagerModeValue
  uploadUsage?: MediaUploadUsageValue
} = {}): PhotoManagerConfig {
  if (mode && PHOTO_MANAGER_CONFIGS[mode]) {
    return PHOTO_MANAGER_CONFIGS[mode]
  }

  if (uploadUsage === MediaUploadUsage.FaviconIcon.Value) {
    return PHOTO_MANAGER_CONFIGS[PhotoManagerMode.UploadOnly]
  }

  return PHOTO_MANAGER_CONFIGS[PhotoManagerMode.Full]
}

function normalizeFolderName(name: string | null | undefined) {
  return String(name || '')
    .trim()
    .toLowerCase()
}

export function getPhotoManagerDefaultUploadDestination(
  destination: PhotoManagerDefaultUploadDestinationValue | null | undefined
): PhotoManagerDefaultUploadDestinationConfig | null {
  if (!destination) {
    return null
  }

  return PHOTO_MANAGER_DEFAULT_UPLOAD_DESTINATIONS[destination] || null
}

export function findDefaultUploadDestinationFolder(
  folders: readonly PhotoFolder[] = [],
  destination?: PhotoManagerDefaultUploadDestinationValue | null
): PhotoFolder | null {
  const defaultDestination = getPhotoManagerDefaultUploadDestination(destination)

  if (!defaultDestination) {
    return null
  }

  return (
    folders.find(
      (folder) => normalizeFolderName(folder?.name) === normalizeFolderName(defaultDestination.name)
    ) || null
  )
}
